import { appData } from "../../app/state/state.js";
import {
    collection,
    getDocs,
    limit,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { updateTaskLinkage } from "../../shared/api/taskLinkage.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    findItem,
    getCollectionName,
    getItemType,
} from "../../shared/utils/itemLookup.js";
import {
    getSortedUsers,
} from "../../shared/utils/userDisplay.js";
import {
    isItemClosed,
} from "../../shared/utils/closedState.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    getBoardRegistryEntryByItemType,
} from "../../shared/board/boardRegistry.js";

const TASK_LINK_OPEN_TASK_LIMIT = 1000;
const TASK_LINK_CONFIRM_BUTTON_CLASSES = [
    "rounded-lg bg-[var(--dfc-brand-primary)] px-4 py-2 text-sm font-semibold",
    "text-[var(--dfc-brand-primary-contrast)] transition-colors",
    "hover:bg-[var(--dfc-brand-primary-hover)] disabled:cursor-not-allowed",
    "disabled:bg-gray-300 disabled:text-gray-500",
].join(" ");
const TASK_LINK_CURRENT_PARENT_OPTIONS = Object.freeze([
    { value: "standalone", label: "Standalone" },
    { value: "patient", label: "Patient tasks" },
    { value: "lab", label: "Lab tasks" },
    { value: "project", label: "Project tasks" },
    { value: "asset", label: "Asset tasks" },
    { value: "maintenance", label: "Maintenance tasks" },
]);
const TASK_LINK_TARGET_PARENT_TYPE_OPTIONS = Object.freeze([
    { value: "none", label: "No parent" },
    { value: "patient", label: "Patient" },
    { value: "lab", label: "Lab" },
    { value: "project", label: "Project" },
    { value: "asset", label: "Asset" },
    { value: "maintenance", label: "Maintenance" },
]);

let taskLinkToolState = createDefaultTaskLinkToolState();
let taskLinkOpenTasks = [];
let taskLinkOpenTasksStatus = "idle";
let taskLinkOpenTasksError = null;
let taskLinkOpenTasksRequestId = 0;

function createDefaultTaskLinkToolState() {
    return {
        currentParentType: "standalone",
        ownerId: "",
        taskId: "",
        targetParentType: "",
        targetParentId: "",
    };
}

/**
 * Resets transient category-settings state such as task-link tool selections.
 *
 * @returns {void}
 */
export function resetTaskRelinkSettingsState() {
    taskLinkToolState = createDefaultTaskLinkToolState();
}

function normalizeTaskLinkParentType(parentType) {
    if (!parentType || parentType === "none") {return null;}
    if (parentType === "standalone") {return "standalone";}
    return parentType.endsWith("s") ? parentType.slice(0, -1) : parentType;
}

function formatParentTypeLabel(parentType) {
    if (!parentType) {return "Parent";}
    return parentType.charAt(0).toUpperCase() + parentType.slice(1);
}

function getTaskCurrentParentType(task) {
    if (!task?.parentId) {return "standalone";}
    return normalizeTaskLinkParentType(task.parentType) ||
        getItemType(task.parentId) || "standalone";
}

function getTaskLinkTaskPool() {
    return taskLinkOpenTasksStatus === "loaded" ? taskLinkOpenTasks : [];
}

function getOpenTasksForTaskLinking() {
    return getTaskLinkTaskPool().filter((task) => !isItemClosed(task));
}

function getTaskLinkOwnerPlaceholder() {
    if (taskLinkOpenTasksStatus === "loading") {return "Loading tasks...";}
    if (taskLinkOpenTasksStatus === "error") {return "Unable to load tasks";}
    return getTaskLinkOwnerOptions().length > 0 ?
        "Select owner" :
        "No matching owners";
}

function getTaskLinkHelperMessage() {
    if (taskLinkOpenTasksStatus === "loading") {
        return "Loading active tasks for all owners.";
    }
    if (taskLinkOpenTasksStatus === "error") {
        return taskLinkOpenTasksError ||
            "Task loading failed. Check the console and try again.";
    }
    return "Owners are filtered to active tasks in the selected current parent group.";
}

async function loadTaskLinkOpenTasks(container) {
    const requestId = taskLinkOpenTasksRequestId + 1;
    taskLinkOpenTasksRequestId = requestId;
    taskLinkOpenTasksStatus = "loading";
    taskLinkOpenTasksError = null;
    taskLinkOpenTasks = [];
    syncTaskLinkingToolUi(container);

    try {
        const tasksQuery = query(
            collection(db, "tasks"),
            where("isClosedResolved", "==", false),
            limit(TASK_LINK_OPEN_TASK_LIMIT),
        );
        const snapshot = await getDocs(tasksQuery);
        if (requestId !== taskLinkOpenTasksRequestId) {return;}

        taskLinkOpenTasks = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
        taskLinkOpenTasksStatus = "loaded";
    } catch (error) {
        if (requestId !== taskLinkOpenTasksRequestId) {return;}

        console.error("Error loading task-linking tasks:", error);
        taskLinkOpenTasks = [];
        taskLinkOpenTasksStatus = "error";
        taskLinkOpenTasksError = "Could not load active tasks for relinking.";
    }

    syncTaskLinkingToolUi(container);
}

function getTaskLinkOwnerOptions() {
    const currentParentType = taskLinkToolState.currentParentType || "standalone";
    const usersById = new Map((appData.users || []).map((user) => [user.id, user]));
    const ownerIds = Array.from(new Set(
        getOpenTasksForTaskLinking()
            .filter((task) => getTaskCurrentParentType(task) === currentParentType)
            .map((task) => task.owner)
            .filter(Boolean),
    ));

    return getSortedUsers(ownerIds.map((ownerId) => {
        const user = usersById.get(ownerId);
        return user || { id: ownerId, name: ownerId, isActive: false };
    }));
}

function getTaskLinkableTasks() {
    const currentParentType = taskLinkToolState.currentParentType || "standalone";
    const ownerId = taskLinkToolState.ownerId || "";
    if (!ownerId) {return [];}

    return getOpenTasksForTaskLinking()
        .filter((task) => task.owner === ownerId)
        .filter((task) => getTaskCurrentParentType(task) === currentParentType)
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
}

function getTaskLinkTargetParents() {
    const targetParentType = normalizeTaskLinkParentType(
        taskLinkToolState.targetParentType || "",
    );
    if (!targetParentType) {return [];}

    const collectionName = getCollectionName(targetParentType);
    const items = appData?.[collectionName] || [];
    return items
        .filter((item) => !isItemClosed(item))
        .sort((a, b) =>
            getTaskLinkParentDisplayName(a, targetParentType)
                .localeCompare(getTaskLinkParentDisplayName(b, targetParentType)));
}

function getTaskLinkParentDisplayName(item, parentType = null) {
    if (!item) {return "";}
    const normalizedParentType = normalizeTaskLinkParentType(parentType) ||
        getItemType(item.id);
    const titleField = getBoardRegistryEntryByItemType(normalizedParentType)
        ?.fields?.titleField;
    return String(
        (titleField ? item[titleField] : null) ||
        item.name ||
        item.title ||
        item.id ||
        "",
    );
}

function getTaskLinkTaskLabel(task) {
    if (!task) {return "Unknown task";}
    const parts = [task.title || "Untitled task"];
    const parent = task.parentId ? findItem(task.parentId) : null;
    const parentLabel = getTaskLinkParentDisplayName(
        parent,
        getTaskCurrentParentType(task),
    );

    if (parentLabel) {
        parts.push(
            `${formatParentTypeLabel(getTaskCurrentParentType(task))}: ${parentLabel}`,
        );
    } else if (!task.parentId) {
        parts.push("Standalone");
    }

    if (task.due_date) {
        parts.push(`Due ${task.due_date}`);
    }

    return parts.join(" · ");
}

function getTaskLinkTargetParentLabel(item, parentType) {
    if (!item) {return "Unknown parent";}
    return `${formatParentTypeLabel(parentType)}: ${getTaskLinkParentDisplayName(item, parentType)}`;
}

function mapTaskLinkOptions(items, getValue, getLabel) {
    return items.map((item) => ({
        value: getValue(item),
        label: getLabel(item),
    }));
}

function renderTaskLinkingToolContent() {
    const targetParents = getTaskLinkTargetParents();
    const selectedTask = getTaskLinkTaskPool().find(
        (task) => task.id === taskLinkToolState.taskId,
    ) || null;
    const selectedTargetParent = targetParents.find(
        (item) => item.id === taskLinkToolState.targetParentId,
    ) || null;

    const taskReady = Boolean(
        taskLinkToolState.ownerId && taskLinkToolState.taskId,
    );
    const hasTargetSelection = taskLinkToolState.targetParentType === "none" ||
        (
            Boolean(taskLinkToolState.targetParentType) &&
            Boolean(taskLinkToolState.targetParentId)
        );

    return `
        <div id="task-linking-tool-content" class="space-y-6">
            <p class="settings-helper-text max-w-3xl">
                Move an active task to a different parent, unlink it to make it standalone, or relink a standalone task.
            </p>

            <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div class="rounded-lg border border-gray-200 p-4">
                    <h5 class="text-sm font-semibold uppercase tracking-wide text-gray-600 mb-4">Task</h5>
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Current parent</label>
                            <div id="task-link-current-parent-type" data-task-link-select></div>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Task owner</label>
                            <div id="task-link-owner-id" data-task-link-select></div>
                            <p id="task-link-owner-helper" class="settings-helper-text">${getTaskLinkHelperMessage()}</p>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Task</label>
                            <div id="task-link-task-id" data-task-link-select></div>
                            <p class="settings-helper-text">Only active tasks are shown.</p>
                        </div>
                    </div>
                </div>

                <div class="rounded-lg border border-gray-200 p-4">
                    <h5 class="text-sm font-semibold uppercase tracking-wide text-gray-600 mb-4">Target Parent</h5>
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Parent type</label>
                            <div id="task-link-target-parent-type" data-task-link-select></div>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Parent item</label>
                            <div id="task-link-target-parent-id" data-task-link-select></div>
                            <p class="settings-helper-text">Only active parent items are shown.</p>
                        </div>

                        <div id="task-link-summary-box" class="settings-task-link-summary">
                            <div id="task-link-selected-task-summary">
                                ${selectedTask
            ? `<strong>Selected task:</strong> ${sanitizeHTML(getTaskLinkTaskLabel(selectedTask))}`
            : "<strong>Selected task:</strong> None"}
                            </div>
                            <div id="task-link-result-summary" class="mt-1">
                                <strong>Result:</strong> ${taskLinkToolState.targetParentType === "none"
            ? "Task will become standalone."
            : selectedTargetParent
                ? sanitizeHTML(
                    getTaskLinkTargetParentLabel(
                        selectedTargetParent,
                        taskLinkToolState.targetParentType,
                    ),
                )
                : "Choose a target parent."}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end">
                <button id="task-link-confirm-btn" type="button" class="${TASK_LINK_CONFIRM_BUTTON_CLASSES}" ${(taskReady && hasTargetSelection) ? "" : "disabled"}>
                    Confirm
                </button>
            </div>
        </div>
    `;
}

function syncTaskLinkingToolUi(container) {
    const currentParentSelect = container.querySelector(
        "#task-link-current-parent-type",
    );
    const ownerSelect = container.querySelector("#task-link-owner-id");
    const ownerHelper = container.querySelector("#task-link-owner-helper");
    const taskSelect = container.querySelector("#task-link-task-id");
    const targetParentTypeSelect = container.querySelector(
        "#task-link-target-parent-type",
    );
    const targetParentIdSelect = container.querySelector(
        "#task-link-target-parent-id",
    );
    const selectedTaskSummary = container.querySelector(
        "#task-link-selected-task-summary",
    );
    const resultSummary = container.querySelector("#task-link-result-summary");
    const confirmBtn = container.querySelector("#task-link-confirm-btn");

    const ownerOptions = getTaskLinkOwnerOptions();
    if (
        taskLinkToolState.ownerId &&
        !ownerOptions.some((user) => user.id === taskLinkToolState.ownerId)
    ) {
        taskLinkToolState.ownerId = "";
        taskLinkToolState.taskId = "";
    }

    const taskOptions = getTaskLinkableTasks();
    if (
        taskLinkToolState.taskId &&
        !taskOptions.some((task) => task.id === taskLinkToolState.taskId)
    ) {
        taskLinkToolState.taskId = "";
    }

    const targetParentOptions = getTaskLinkTargetParents();
    if (
        taskLinkToolState.targetParentId &&
        !targetParentOptions.some((item) =>
            item.id === taskLinkToolState.targetParentId)
    ) {
        taskLinkToolState.targetParentId = "";
    }

    if (currentParentSelect) {
        createSelectControl({
            root: currentParentSelect,
            accessibleName: "Current parent",
            value: taskLinkToolState.currentParentType || "standalone",
            options: TASK_LINK_CURRENT_PARENT_OPTIONS,
            onChange: (value) => {
                taskLinkToolState.currentParentType = value || "standalone";
                taskLinkToolState.ownerId = "";
                taskLinkToolState.taskId = "";
                syncTaskLinkingToolUi(container);
            },
        });
    }

    if (ownerSelect) {
        createSelectControl({
            root: ownerSelect,
            accessibleName: "Task owner",
            value: taskLinkToolState.ownerId || "",
            options: mapTaskLinkOptions(
                ownerOptions,
                (user) => user.id,
                (user) => `${user.name || user.email}${user.isActive === false ? " (inactive)" : ""}`,
            ),
            searchable: true,
            clearable: true,
            placeholder: getTaskLinkOwnerPlaceholder(),
            emptyMessage: getTaskLinkOwnerPlaceholder(),
            onChange: (value) => {
                taskLinkToolState.ownerId = value || "";
                taskLinkToolState.taskId = "";
                syncTaskLinkingToolUi(container);
            },
        });
    }

    if (ownerHelper) {
        ownerHelper.textContent = getTaskLinkHelperMessage();
    }

    if (taskSelect) {
        createSelectControl({
            root: taskSelect,
            accessibleName: "Task",
            value: taskLinkToolState.taskId || "",
            options: mapTaskLinkOptions(
                taskOptions,
                (task) => task.id,
                (task) => getTaskLinkTaskLabel(task),
            ),
            searchable: true,
            clearable: true,
            disabled: !taskLinkToolState.ownerId,
            placeholder: taskLinkToolState.ownerId ? "Select task" : "Select owner first",
            emptyMessage: taskLinkToolState.ownerId ? "No active tasks" : "Select owner first",
            onChange: (value) => {
                taskLinkToolState.taskId = value || "";
                syncTaskLinkingToolUi(container);
            },
        });
    }

    const normalizedTargetType = normalizeTaskLinkParentType(
        taskLinkToolState.targetParentType || "",
    );
    const targetParentEnabled = Boolean(
        taskLinkToolState.targetParentType &&
        taskLinkToolState.targetParentType !== "none",
    );

    if (targetParentTypeSelect) {
        createSelectControl({
            root: targetParentTypeSelect,
            accessibleName: "Parent type",
            value: taskLinkToolState.targetParentType || "",
            options: TASK_LINK_TARGET_PARENT_TYPE_OPTIONS,
            clearable: true,
            placeholder: "Select target",
            onChange: (value) => {
                taskLinkToolState.targetParentType = value || "";
                taskLinkToolState.targetParentId = "";
                syncTaskLinkingToolUi(container);
            },
        });
    }

    if (targetParentIdSelect) {
        createSelectControl({
            root: targetParentIdSelect,
            accessibleName: "Parent item",
            value: taskLinkToolState.targetParentId || "",
            options: mapTaskLinkOptions(
                targetParentOptions,
                (item) => item.id,
                (item) => getTaskLinkTargetParentLabel(item, normalizedTargetType),
            ),
            searchable: true,
            clearable: true,
            disabled: !targetParentEnabled,
            placeholder: targetParentEnabled
                ? (targetParentOptions.length > 0 ? "Select parent item" : "No active parent items")
                : "Select parent type first",
            emptyMessage: targetParentEnabled ? "No active parent items" : "Select parent type first",
            onChange: (value) => {
                taskLinkToolState.targetParentId = value || "";
                syncTaskLinkingToolUi(container);
            },
        });
    }

    const selectedTask = getTaskLinkTaskPool().find(
        (task) => task.id === taskLinkToolState.taskId,
    ) || null;
    const selectedTargetParent = targetParentOptions.find(
        (item) => item.id === taskLinkToolState.targetParentId,
    ) || null;

    if (selectedTaskSummary) {
        selectedTaskSummary.innerHTML = selectedTask
            ? `<strong>Selected task:</strong> ${sanitizeHTML(getTaskLinkTaskLabel(selectedTask))}`
            : "<strong>Selected task:</strong> None";
    }

    if (resultSummary) {
        let resultText = "Choose a target parent.";
        if (taskLinkToolState.targetParentType === "none") {
            resultText = "Task will become standalone.";
        } else if (selectedTargetParent) {
            resultText = sanitizeHTML(
                getTaskLinkTargetParentLabel(
                    selectedTargetParent,
                    normalizeTaskLinkParentType(taskLinkToolState.targetParentType),
                ),
            );
        }
        resultSummary.innerHTML = `<strong>Result:</strong> ${resultText}`;
    }

    if (confirmBtn) {
        const taskReady = Boolean(
            taskLinkToolState.ownerId && taskLinkToolState.taskId,
        );
        const hasTargetSelection = taskLinkToolState.targetParentType === "none" ||
            (
                Boolean(taskLinkToolState.targetParentType) &&
                Boolean(taskLinkToolState.targetParentId)
            );
        confirmBtn.disabled = !(taskReady && hasTargetSelection);
    }

    bindNativeFieldBehavior(container.querySelector("#task-linking-tool-content") || container);
}

/**
 * Renders and wires the admin task relinking tool.
 * @param {HTMLElement} container Settings tab container.
 * @returns {void}
 */
export function renderTaskLinkingTool(container) {
    const toolContainer = container.querySelector("#task-linking-tool-wrapper");
    if (!toolContainer) {return;}

    if (taskLinkOpenTasksStatus === "idle") {
        taskLinkOpenTasksStatus = "loading";
    }
    toolContainer.innerHTML = renderTaskLinkingToolContent();
    attachTaskLinkingToolListeners(container);
    syncTaskLinkingToolUi(container);
    if (taskLinkOpenTasksStatus === "loading") {
        loadTaskLinkOpenTasks(container);
    }
}

function attachTaskLinkingToolListeners(container) {
    const confirmBtn = container.querySelector("#task-link-confirm-btn");

    if (!confirmBtn) {return;}

    confirmBtn.addEventListener("click", async () => {
        const selectedTask = getTaskLinkTaskPool().find(
            (task) => task.id === taskLinkToolState.taskId,
        );

        if (!selectedTask) {
            openConfirmationModal(
                "Selection Required",
                "Choose a task first.",
                () => {},
                false,
                "OK",
            );
            return;
        }

        const targetParentType = taskLinkToolState.targetParentType || "";
        if (!targetParentType) {
            openConfirmationModal(
                "Selection Required",
                "Choose a target parent type first.",
                () => {},
                false,
                "OK",
            );
            return;
        }

        if (targetParentType !== "none" && !taskLinkToolState.targetParentId) {
            openConfirmationModal(
                "Selection Required",
                "Choose a target parent item first.",
                () => {},
                false,
                "OK",
            );
            return;
        }

        confirmBtn.disabled = true;
        confirmBtn.textContent = "Updating...";

        try {
            const result = await updateTaskLinkage({
                taskId: selectedTask.id,
                targetParentType,
                targetParentId: targetParentType === "none"
                    ? null
                    : taskLinkToolState.targetParentId,
            });

            if (result?.changed === false) {
                openConfirmationModal(
                    "No Change",
                    "That task is already linked to the selected parent.",
                    () => {},
                    false,
                    "OK",
                );
                return;
            }

            resetTaskRelinkSettingsState();
            taskLinkOpenTasks = [];
            taskLinkOpenTasksStatus = "idle";
            taskLinkOpenTasksError = null;
            renderTaskLinkingTool(container);

            const targetLabel = result?.targetParent?.name
                ? `${formatParentTypeLabel((result.targetParent.type || "").replace(/s$/, ""))}: ${result.targetParent.name}`
                : "Standalone";
            openConfirmationModal(
                "Task Updated",
                `<strong>${sanitizeHTML(result?.taskTitle || selectedTask.title || "Task")}</strong> is now linked to <strong>${sanitizeHTML(targetLabel)}</strong>.`,
                () => {},
                false,
                "OK",
            );
        } catch (error) {
            const inaccessibleUsers = error?.details?.inaccessibleUsers;
            if (
                error?.code?.includes("failed-precondition") &&
                Array.isArray(inaccessibleUsers) &&
                inaccessibleUsers.length > 0
            ) {
                const targetParentName = error?.details?.targetParentName ||
                    "the selected parent";
                openConfirmationModal(
                    "Access Conflict",
                    `
                        <p>The task could not be moved because these linked users do not have access to <strong>${sanitizeHTML(targetParentName)}</strong>:</p>
                        <ul class="mt-3 list-disc space-y-1 pl-5">
                            ${inaccessibleUsers.map((user) => `<li>${sanitizeHTML(user.name || user.id)}</li>`).join("")}
                        </ul>
                        <p class="mt-3">Resolve their access first, then try again.</p>
                    `,
                    () => {},
                    false,
                    "OK",
                );
            } else {
                openConfirmationModal(
                    "Error",
                    sanitizeHTML(error?.message || "Failed to update task linkage."),
                    () => {},
                    false,
                    "OK",
                );
            }
        } finally {
            const refreshedConfirmBtn = container.querySelector(
                "#task-link-confirm-btn",
            );
            if (refreshedConfirmBtn) {
                refreshedConfirmBtn.textContent = "Confirm";
            }
        }
    });
}
